import { Component } from '@theme/component';

/**
 * @typedef {Object} CollapsibleTextRefs
 * @property {HTMLDetailsElement} details - The disclosure wrapping the whole component
 * @property {HTMLElement} body - Everything below the summary; this is what animates
 */

/**
 * A custom element that shows or hides its content in full — no clamped preview.
 * Collapsed, only the summary (the "expand" button) is visible; expanded, the whole
 * content plus a "collapse" button at the bottom.
 *
 * The open/closed state lives on the native `<details>`, so the component still works
 * without JS (the summary toggles it) and the content stays in the DOM for crawlers.
 * This class only adds what `<details>` can't do on its own: the bottom collapse button
 * and a height transition.
 *
 * @extends {Component<CollapsibleTextRefs>}
 */
class CollapsibleText extends Component {
  requiredRefs = ['details', 'body'];

  /** @type {Animation | undefined} */
  #animation;

  /** @constant {number} */
  #animationSpeed = 300;

  get #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  connectedCallback() {
    super.connectedCallback();
    this.refs.details.addEventListener('click', this.#onDetailsClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.details.removeEventListener('click', this.#onDetailsClick);
    this.#animation?.cancel();
  }

  /**
   * The native summary toggle snaps the content open and shut. Intercept it so the
   * body can animate instead, and let #expand/#collapse own the `open` attribute.
   *
   * Bound on the `<details>` rather than the `<summary>` so clicks on anything the
   * merchant nests inside the label still count.
   *
   * @param {MouseEvent} event
   */
  #onDetailsClick = (event) => {
    const target = /** @type {Element | null} */ (event.target);
    if (!target?.closest('summary')) return;

    event.preventDefault();

    if (this.refs.details.open) {
      this.#collapse();
    } else {
      this.#expand();
    }
  };

  /**
   * Collapses the content. Wired to the bottom button via `on:click`.
   * @param {Event} [event]
   */
  collapse = (event) => {
    event?.preventDefault();
    this.#collapse();
  };

  #expand = () => {
    const { details, body } = this.refs;
    if (details.open) return;

    // Open first so the body is rendered and can be measured.
    details.open = true;

    if (this.#prefersReducedMotion) return;
    this.#animateHeight(0, body.scrollHeight);
  };

  #collapse = () => {
    const { details, body } = this.refs;
    if (!details.open) return;

    if (this.#prefersReducedMotion) {
      details.open = false;
      return;
    }

    // Closing `details` mid-animation would unrender the body, so the attribute is
    // dropped only once the height has finished travelling to zero.
    this.#animateHeight(body.scrollHeight, 0, () => {
      details.open = false;
    });
  };

  /**
   * Animates the body's height between two values.
   * @param {number} startHeight
   * @param {number} endHeight
   * @param {() => void} [onFinish]
   */
  #animateHeight = (startHeight, endHeight, onFinish) => {
    const { body } = this.refs;

    this.#animation?.cancel();
    body.style.overflow = 'hidden';

    this.#animation = body.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: this.#animationSpeed, easing: 'ease-in-out' }
    );

    this.#animation.onfinish = () => {
      body.style.removeProperty('overflow');
      onFinish?.();
    };
  };
}

if (!customElements.get('collapsible-text')) {
  customElements.define('collapsible-text', CollapsibleText);
}

export default CollapsibleText;
